import type { Edge, Node } from '@xyflow/react';
import type * as Api from '@ViewModels';
import { EDGE_TYPES, type BranchEdgeData, type PhantomAddPathEdgeData } from '../types/edges';
import { NODE_TYPES, type BaseNodeData } from '../types';
import type { AutomationStepNodeData, ParentCaseInfo, SegmentNodeData } from '../types/stepNodes';
import { BRANCH_MARGIN, SEGMENT_NODE } from './layoutConstants';
import { applySwitchBranchLayout } from './layoutEngine';
import { createSegmentNode } from './nodeFactory';
import { buildSwitchOutputHandles } from './optimisticSwitchSync';

interface ApplyPersistedSwitchCaseGraphUpdateParams {
	nodes: Node<BaseNodeData>[];
	edges: Edge[];
	switchNodeId: string;
	persistedSwitchStep: Api.ISwitchAutomationStep;
}

interface SwitchCaseDescriptor {
	caseIndex: number;
	caseName: string;
	node: Node<SegmentNodeData>;
}

function findLocalSwitchNode(switchNodeId: string, nodes: Node<BaseNodeData>[]): Node<BaseNodeData> | undefined {
	const parsedStepId = switchNodeId.match(/^step-(.+)$/)?.[1];

	return nodes.find(node => {
		if (node.id === switchNodeId) {
			return true;
		}

		if (!parsedStepId) {
			return false;
		}

		const stepId = (node.data as { stepId?: unknown } | undefined)?.stepId;
		return typeof stepId === 'string' && stepId === parsedStepId;
	});
}

function getCaseDisplayName(caseStatement: Api.IAutomationStepCaseStatement, caseIndex: number): string {
	return caseStatement.isDefault ? 'Default' : caseStatement.name || `Case ${caseIndex + 1}`;
}

function isSegmentNodeForSwitch(node: Node<BaseNodeData>, switchNodeId: string): node is Node<SegmentNodeData> {
	if (node.type !== NODE_TYPES.SEGMENT) {
		return false;
	}

	const segmentData = node.data as SegmentNodeData;
	return segmentData.parentSwitchNodeId === switchNodeId;
}

function isBranchOwnedBySwitch(edge: Edge, ownerIds: Set<string>, segmentIds: Set<string>): boolean {
	if (edge.type !== EDGE_TYPES.BRANCH) {
		return false;
	}

	const edgeSwitchNodeId = (edge.data as { switchNodeId?: unknown } | undefined)?.switchNodeId;
	return (typeof edgeSwitchNodeId === 'string' && ownerIds.has(edgeSwitchNodeId)) || segmentIds.has(edge.target);
}

function isPhantomEdgeOwnedBySwitch(edge: Edge, ownerIds: Set<string>, phantomTargetId: string): boolean {
	if (edge.type !== EDGE_TYPES.PHANTOM_ADD_PATH) {
		return false;
	}

	const edgeSwitchNodeId = (edge.data as { switchNodeId?: unknown } | undefined)?.switchNodeId;
	return (typeof edgeSwitchNodeId === 'string' && ownerIds.has(edgeSwitchNodeId)) || edge.target === phantomTargetId;
}

function mergeParentCaseInfo(
	node: Node<BaseNodeData>,
	ownerIds: Set<string>,
	nextCaseById: Map<string, Omit<ParentCaseInfo, 'globalSegmentOrdinal' | 'segmentPathKey'>>,
	localSwitchNodeId: string
): Node<BaseNodeData> {
	const parentCaseInfo = (node.data as { parentCaseInfo?: ParentCaseInfo } | undefined)?.parentCaseInfo;
	if (!parentCaseInfo || parentCaseInfo.isBehaviorCase) {
		return node;
	}

	if (
		!parentCaseInfo.caseId ||
		!parentCaseInfo.parentSwitchNodeId ||
		!ownerIds.has(parentCaseInfo.parentSwitchNodeId)
	) {
		return node;
	}

	const nextParentCaseInfo = nextCaseById.get(parentCaseInfo.caseId);
	if (!nextParentCaseInfo) {
		return node;
	}

	return {
		...node,
		data: {
			...node.data,
			parentCaseInfo: {
				...parentCaseInfo,
				...nextParentCaseInfo,
				parentSwitchNodeId: localSwitchNodeId,
			},
		},
	};
}

/**
 * Applies a local graph update for a persisted switch-case mutation.
 *
 * This is used when the server has already created the new case and returned the
 * updated switch payload, but the root flow refetch has not completed yet.
 * Updating the local graph immediately keeps the phantom add-path button feeling
 * responsive while the canonical refetch catches up in the background.
 */
export function applyPersistedSwitchCaseGraphUpdate({
	nodes,
	edges,
	switchNodeId,
	persistedSwitchStep,
}: ApplyPersistedSwitchCaseGraphUpdateParams): {
	nodes: Node<BaseNodeData>[];
	edges: Edge[];
	addedSegmentId?: string;
} {
	const localSwitchNode = findLocalSwitchNode(switchNodeId, nodes);
	if (!localSwitchNode) {
		return { nodes, edges };
	}

	const localSwitchNodeId = localSwitchNode.id;
	const ownerIds = new Set([switchNodeId, localSwitchNodeId]);
	const existingSegments = nodes
		.filter(node => isSegmentNodeForSwitch(node, localSwitchNodeId))
		.sort((a, b) => a.data.segmentIndex - b.data.segmentIndex);
	const existingSegmentIds = new Set(existingSegments.map(node => node.id));
	const existingCaseIds = new Set(existingSegments.map(node => node.data.caseId).filter(Boolean));
	const existingSegmentsByCaseId = new Map(existingSegments.map(node => [node.data.caseId, node] as const));
	const defaultSegment =
		existingSegments.find(node => node.data.isDefault) ?? existingSegments[existingSegments.length - 1] ?? null;
	const basePosition = defaultSegment?.position ?? localSwitchNode.position;
	const phantomTargetId = `phantom-target-${localSwitchNodeId}`;

	const switchBranchEdges = edges.filter(edge => isBranchOwnedBySwitch(edge, ownerIds, existingSegmentIds));
	const existingBranchEdgeByTarget = new Map(switchBranchEdges.map(edge => [edge.target, edge] as const));
	const existingPhantomEdge = edges.find(edge => isPhantomEdgeOwnedBySwitch(edge, ownerIds, phantomTargetId));
	const visualSourceId = switchBranchEdges[0]?.source ?? existingPhantomEdge?.source ?? localSwitchNodeId;

	const caseDescriptors: SwitchCaseDescriptor[] = (persistedSwitchStep.cases || []).map((caseStatement, caseIndex) => {
		const existingSegment = caseStatement.id ? existingSegmentsByCaseId.get(caseStatement.id) : undefined;
		const createdSegment = createSegmentNode(
			caseStatement,
			caseIndex,
			localSwitchNodeId,
			existingSegment?.position ?? basePosition,
			caseStatement.automationTemplateId ?? existingSegment?.data.childTemplateId
		);

		const nextSegment: Node<SegmentNodeData> = existingSegment
			? {
					...existingSegment,
					position: existingSegment.position,
					data: {
						...createdSegment.data,
						isExpanded: existingSegment.data.isExpanded ?? createdSegment.data.isExpanded,
						contactColors: existingSegment.data.contactColors,
						contactCount: existingSegment.data.contactCount,
						globalSegmentOrdinal: existingSegment.data.globalSegmentOrdinal,
						segmentPathKey: existingSegment.data.segmentPathKey,
					},
				}
			: createdSegment;

		return {
			caseIndex,
			caseName: getCaseDisplayName(caseStatement, caseIndex),
			node: nextSegment,
		};
	});

	const nextCaseById = new Map<string, Omit<ParentCaseInfo, 'globalSegmentOrdinal' | 'segmentPathKey'>>();
	for (const descriptor of caseDescriptors) {
		const segmentData = descriptor.node.data;
		nextCaseById.set(segmentData.caseId, {
			caseIndex: descriptor.caseIndex,
			caseName: descriptor.caseName,
			isDefault: segmentData.isDefault,
			caseColor: segmentData.caseColor,
			parentSwitchNodeId: localSwitchNodeId,
			caseId: segmentData.caseId,
			isBehaviorCase: false,
		});
	}

	const nextNodes = nodes
		.filter(
			node =>
				node.id !== localSwitchNodeId && node.id !== phantomTargetId && !isSegmentNodeForSwitch(node, localSwitchNodeId)
		)
		.map(node => mergeParentCaseInfo(node, ownerIds, nextCaseById, localSwitchNodeId));

	const updatedSwitchNode: Node<BaseNodeData> = {
		...localSwitchNode,
		data: {
			...localSwitchNode.data,
			stepId: persistedSwitchStep.id || (localSwitchNode.data as { stepId?: string } | undefined)?.stepId,
			automationStep: persistedSwitchStep,
			summary: `${persistedSwitchStep.cases?.length ?? 0} branches`,
			outputHandles: buildSwitchOutputHandles(persistedSwitchStep.cases),
			isValid: (persistedSwitchStep.cases?.length ?? 0) > 0,
			validationErrors: (persistedSwitchStep.cases?.length ?? 0) > 0 ? [] : ['At least one branch is required'],
			stepData: {
				...((localSwitchNode.data as AutomationStepNodeData | undefined)?.stepData || {}),
				name:
					persistedSwitchStep.name ||
					(localSwitchNode.data as AutomationStepNodeData | undefined)?.stepData?.name ||
					'Conditional Branch',
				schedule:
					persistedSwitchStep.schedule ||
					(localSwitchNode.data as AutomationStepNodeData | undefined)?.stepData?.schedule,
				hasContent: (persistedSwitchStep.cases?.length ?? 0) > 0,
			},
		},
	};

	const preservedEdges = edges.filter(edge => {
		if (isBranchOwnedBySwitch(edge, ownerIds, existingSegmentIds)) {
			return false;
		}
		if (isPhantomEdgeOwnedBySwitch(edge, ownerIds, phantomTargetId)) {
			return false;
		}
		return true;
	});

	const totalCases = caseDescriptors.length;
	const nextBranchEdges: Edge[] = caseDescriptors.map(descriptor => {
		const segmentData = descriptor.node.data;
		const existingEdge = existingBranchEdgeByTarget.get(descriptor.node.id);
		const edgeData = (existingEdge?.data as BranchEdgeData | undefined) || undefined;

		return {
			...(existingEdge || {}),
			id: existingEdge?.id || `${visualSourceId}-to-${descriptor.node.id}`,
			source: visualSourceId,
			sourceHandle: existingEdge?.sourceHandle || 'default',
			target: descriptor.node.id,
			type: EDGE_TYPES.BRANCH,
			data: {
				...edgeData,
				condition: persistedSwitchStep.cases?.[descriptor.caseIndex]?.filter,
				caseId: segmentData.caseId,
				caseIndex: descriptor.caseIndex,
				totalCases,
				isDefault: segmentData.isDefault,
				caseName: descriptor.caseName,
				caseColor: segmentData.caseColor,
				switchNodeId: localSwitchNodeId,
			},
		};
	});

	const phantomTargetNode =
		nodes.find(node => node.id === phantomTargetId) ||
		({
			id: phantomTargetId,
			type: NODE_TYPES.PHANTOM_TARGET,
			position: {
				x: basePosition.x + SEGMENT_NODE.WIDTH + BRANCH_MARGIN,
				y: basePosition.y,
			},
			width: 1,
			height: 1,
			data: {
				label: '',
				category: 'action',
			},
		} satisfies Node<BaseNodeData>);

	const nextPhantomEdge: Edge<PhantomAddPathEdgeData> = {
		...(existingPhantomEdge || {}),
		id: existingPhantomEdge?.id || `phantom-add-path-${localSwitchNodeId}`,
		source: visualSourceId,
		sourceHandle: existingPhantomEdge?.sourceHandle || 'default',
		target: phantomTargetNode.id,
		type: EDGE_TYPES.PHANTOM_ADD_PATH,
		data: {
			...((existingPhantomEdge?.data as PhantomAddPathEdgeData | undefined) || {}),
			switchNodeId: localSwitchNodeId,
			segmentCount: totalCases,
		},
	};

	const graphNodes = [
		...nextNodes,
		updatedSwitchNode,
		...caseDescriptors.map(descriptor => descriptor.node),
		phantomTargetNode,
	];
	const graphEdges = [...preservedEdges, ...nextBranchEdges, nextPhantomEdge];
	const laidOutNodes = applySwitchBranchLayout(graphNodes, graphEdges);

	const addedDescriptor = caseDescriptors.find(descriptor => !existingCaseIds.has(descriptor.node.data.caseId));

	return {
		nodes: laidOutNodes,
		edges: graphEdges,
		addedSegmentId: addedDescriptor?.node.id,
	};
}
